import * as Actions from '../actions'
import * as Constants from '../constants'
import * as Processes from '@rushplay/processes'
import * as R from 'ramda'
import * as Request from '../request'

import {bind} from 'redux-effects'
import {createPaymentMethodId} from '../create-provider-id'

function transformTransactionType(txType) {
  switch (txType) {
    case 'deposit': {
      return Constants.TransactionType.DEPOSIT
    }
    case 'withdraw': {
      return Constants.TransactionType.WITHDRAWAL
    }

    default: {
      throw new Error('Unknown transaction type')
    }
  }
}

function transformPaymentMethods(paymentMethods, txType) {
  return R.reduce(
    (methods, method) => {
      const transactionType = transformTransactionType(method.transaction_type)

      if (transactionType === txType) {
        const backendType = Constants.BackendType.HELLOCLEVER

        return R.append(
          {
            accounts: [],
            backendType,
            canAddAccount: true,
            id: createPaymentMethodId({
              backendType,
              providerType: method.provider,
              transactionType,
            }),
            limit: {
              max: `${method.limits.max_cents / 100}`,
              min: `${method.limits.min_cents / 100}`,
            },
            providerType: method.provider,
            transactionType,
          },
          methods
        )
      }

      return methods
    },
    [],
    paymentMethods
  )
}

export function fetchPaymentMethods(params) {
  const processId = `helloclever/fetch-payments-methods`

  return [
    Processes.start(processId),
    bind(
      Request.createRequest({
        backendType: Constants.BackendType.HELLOCLEVER,
        requestData: {
          countryCode: params.countryCode,
        },
        requestHeaders: {
          Accept: 'application/vnd.casinosaga.v1, application/json',
          Authorization: R.defaultTo('', params.sessionId),
        },
        requestMethod: 'GET',
        urlTemplate: '/hello_clever/payment_methods{?countryCode}',
      }),
      (res) => [
        Actions.updatePaymentMethods(
          transformPaymentMethods(res.value, params.transactionType)
        ),
        Processes.stop(processId),
      ],
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          // Pass real error to console for easier debugging
          // eslint-disable-next-line no-console
          console.error(error)
        }

        return [
          Actions.updatePaymentMethods(
            new Error('errors.hello-clever.fetch-payment-methods-failure')
          ),
          Processes.stop(processId),
        ]
      }
    ),
  ]
}

export function performTransaction(params, transactionParams) {
  const processId = `perform-transaction-${params.paymentMethodId}`
  return [
    Processes.start(processId),
    bind(
      Request.createRequest({
        backendType: Constants.BackendType.HELLOCLEVER,
        bodyTemplate: {
          amount_cents: true,
          transaction_type: true,
          payId: false,
        },
        requestData: R.merge(transactionParams, {
          amount_cents: params.amountCents,
          transaction_type: params.transactionType,
        }),
        requestMethod: 'POST',
        requestHeaders: {
          Accept: 'application/vnd.casinosaga.v1, application/json',
          Authorization: R.defaultTo('', params.sessionId),
        },
        urlTemplate: '/hello_clever/payment',
      }),
      (res) => {
        const payId = res.value.request_payid
        const nonceCode = res.value.nonce

        return [
          R.isNil(payId)
            ? Actions.startTransaction({
                transactionState: Constants.TransactionState.SUCCESSFUL,
              })
            : Actions.startTransaction({
                redirectOutput: {
                  payId: payId,
                  nonceCode: nonceCode,
                  container: 'window',
                },
                transactionId: res.value.reference,
                paymentMethodId: params.paymentMethodId,
                transactionState: Constants.TransactionState.WAITING_INPUT,
              }),
          Processes.stop(processId),
        ]
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          // Pass real error to console for easier debugging
          // eslint-disable-next-line no-console
          console.error(error)
        }

        return [
          Actions.completeTransaction(
            new Error('errors.hello-clever.perform-transaction-failure')
          ),
          Processes.stop(processId),
        ]
      }
    ),
  ]
}

/**
 * Request transaction state update
 *
 * @param {Object} params
 * @param {MerchantId} params.merchantId
 * @param {SessionId} params.sessionId
 * @param {string} params.transactionId
 * @param {UserId} params.userId
 * @returns Redux Effects fetch action
 */
export function fetchTransactionState(params) {
  return [
    bind(
      Request.createRequest({
        backendType: Constants.BackendType.HELLOCLEVER,
        requestData: {
          id: params.transactionId,
        },
        requestMethod: 'GET',
        requestHeaders: {
          Accept: 'application/vnd.casinosaga.v1, application/json',
          Authorization: R.defaultTo('', params.sessionId),
        },
        urlTemplate: '/hello_clever/payment{?id}',
      }),
      (res) => {
        if (res.value.status == 'accepted') {
          return [
            Actions.updateTransaction({
              transactionState: Constants.TransactionState.SUCCESSFUL,
            }),
          ]
        }
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          // Pass real error to console for easier debugging
          // eslint-disable-next-line no-console
          console.error(error)
        }
      }
    ),
  ]
}
